<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="text-xs text-gray-800">
    <span v-show="parsedDescription" v-html="parsedDescription" />
    <div v-if="isDescriptionOverflowing" class="flex items-center space-x-1">
      <span v-if="!isDescriptionExpanded">...</span>
      <CButton variant="plain" class="h-6 p-0 font-bold underline" @click="toggleDescriptionExpanded()">
        {{ isDescriptionExpanded ? t('show_less') : t('show_more') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { documentToHtmlString, Options } from '@contentful/rich-text-html-renderer';
import * as contentfulRichText from '@contentful/rich-text-types';
import { useI18n } from 'vue-i18n';

type Props = {
  contentfulDescription?: contentfulRichText.Document | {};
};

const props = withDefaults(defineProps<Props>(), {
  contentfulDescription: () => ({}),
});

const { INLINES, BLOCKS } = contentfulRichText;

const { public: config } = useRuntimeConfig();
const { t } = useI18n();

const isDescriptionExpanded = ref(false);
const isDescriptionOverflowing = ref(false);

/** The rich text ctf field data is returned as a JSON-array of nodes -- [{nodeType: 'paragraph': {..}}]
 *  @documentToHtmlString transforms rich text to HTML-format
 *  @renderNode an optional custom renderer for a node type, here to specify styling of the resulting html
 */
const parsedTextBlock = (text: contentfulRichText.Document, isDescriptionExpanded: boolean) => {
  const options: Options = {
    renderNode: {
      [INLINES.HYPERLINK]: ({ content, data }, next) => {
        return `<a class="font-bold underline cursor-pointer" href="${config.baseUrl}/${data.uri}">${next(
          content,
        )}</a>`;
      },
      [BLOCKS.PARAGRAPH]: ({ content }, next) => {
        return isDescriptionExpanded
          ? `<p class="description">${next(content)}</p>`
          : `<p class="overflow-hidden description max-h-8 md:max-h-12">${next(content)}</p>`;
      },
    },
  };
  return documentToHtmlString(text, options);
};

const parsedDescription = computed(() =>
  parsedTextBlock(props.contentfulDescription as contentfulRichText.Document, isDescriptionExpanded.value),
);

const toggleDescriptionExpanded = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
};

onMounted(() => {
  if (process.client) {
    const descriptionElement = document.querySelector('p.description') as HTMLDivElement;
    if (descriptionElement?.offsetHeight < descriptionElement?.scrollHeight) {
      isDescriptionOverflowing.value = true;
    } else isDescriptionOverflowing.value = false;
  }
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductListingPageDescription',
});
</script>
